import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, CheckCircle2, ChevronDown, ChevronRight, Lock, Loader2, Map as MapIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { Badge, CAMPAIGN_STATUS, fmtDate, money, ProgressBar } from "./shared";
import { DayItemsEditor } from "./DayItemsEditor";
import { AddonsAdmin } from "./AddonsAdmin";

const FLAGS = [["contacts", "Contatti"], ["ig", "IG"], ["tiktok", "TikTok"], ["views", "Visual"], ["clicks", "Click"]];
const METRICS = [["contacts", "Contatti"], ["ig", "Instagram"], ["tiktok", "TikTok"], ["views", "Visualizzazioni"], ["clicks", "Click"]];

const actionBtn = "h-10 px-4 text-sm inline-flex items-center gap-2 disabled:opacity-40";

const CompleteButton = ({ onConfirm, busy }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid="mc-complete-btn" disabled={busy} className={`${actionBtn} bg-[#10b981] hover:bg-[#059669] text-white`}>
        <CheckCircle2 className="w-4 h-4" /> Segna campagna come COMPLETATA
      </button>
    </AlertDialogTrigger>
    <AlertDialogContent className="bg-white border-[#d5dbde] text-[#2B3036]">
      <AlertDialogHeader>
        <AlertDialogTitle>Chiudere la campagna?</AlertDialogTitle>
        <AlertDialogDescription className="text-[#5b6168]">
          Gli inserimenti verranno bloccati, il cliente riceverà la notifica "La tua campagna è terminata" e vedrà il riepilogo finale dei soli dati flaggati.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel className="bg-transparent border-[#d5dbde] text-[#2B3036] hover:bg-[#f4f6f7] hover:text-[#E0262C]">Annulla</AlertDialogCancel>
        <AlertDialogAction data-testid="mc-complete-confirm" onClick={onConfirm} className="bg-[#10b981] hover:bg-[#059669]">Completa</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const NumberCell = ({ value, disabled, onSave, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <input data-testid={testid} type="number" min={0} value={v} disabled={disabled}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => Number(v) !== Number(value) && onSave(Math.max(0, parseInt(v || "0", 10)))}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="w-20 h-8 bg-[#f4f6f7] border border-[#d5dbde] text-[#2B3036] text-sm font-mono px-2 disabled:opacity-40 disabled:cursor-not-allowed" />
  );
};

const DayRow = ({ row, campaign, onPatch, onReload }) => {
  const [open, setOpen] = useState(false);
  const locked = !row.editable;
  return (
    <>
      <tr data-testid={`day-row-${row.day}`} className={`border-t border-[#d5dbde] ${locked ? "opacity-60" : "hover:bg-[#f4f6f7]"}`}>
        <td className="px-3 py-3">
          <button data-testid={`day-expand-${row.day}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 font-display text-lg">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} {row.day}
          </button>
          <div className="text-[10px] font-mono text-[#6b7280]">{fmtDate(row.date)}</div>
        </td>
        {["contacts", "ig", "tiktok"].map((k) => (
          <td key={k} className="px-3 py-3">
            <NumberCell testid={`day-${row.day}-${k}`} value={row.counts[k]} disabled={locked} onSave={(n) => onPatch(row.day, { [`${k}_n`]: n })} />
            <div className="text-[9px] font-mono text-[#6b7280] mt-1" data-testid={`day-${row.day}-${k}-list`}>{(row[k] || []).length} in elenco</div>
          </td>
        ))}
        <td className="px-3 py-3"><NumberCell testid={`day-${row.day}-views`} value={row.views} disabled={locked} onSave={(views) => onPatch(row.day, { views })} /></td>
        <td className="px-3 py-3"><NumberCell testid={`day-${row.day}-clicks`} value={row.clicks} disabled={locked} onSave={(clicks) => onPatch(row.day, { clicks })} /></td>
        <td className="px-3 py-3">
          <div className="flex gap-1.5 mb-2">
            <button data-testid={`flags-all-${row.day}`} disabled={locked}
              onClick={() => onPatch(row.day, { flags: Object.fromEntries(FLAGS.map(([k]) => [k, true])) })}
              className="text-[9px] font-mono uppercase border border-[#d5dbde] hover:border-[#10b981] hover:text-[#10b981] text-[#5b6168] px-2 py-0.5 disabled:opacity-30">Rendi tutto visibile</button>
            <button data-testid={`flags-none-${row.day}`} disabled={locked}
              onClick={() => onPatch(row.day, { flags: Object.fromEntries(FLAGS.map(([k]) => [k, false])) })}
              className="text-[9px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] hover:text-[#E0262C] text-[#5b6168] px-2 py-0.5 disabled:opacity-30">Nascondi tutto</button>
          </div>
          <div className="flex gap-3">
            {FLAGS.map(([k, label]) => (
              <label key={k} className="flex flex-col items-center gap-1 text-[9px] font-mono uppercase text-[#6b7280]">
                <Checkbox data-testid={`flag-${row.day}-${k}`} checked={!!row.flags[k]} disabled={locked}
                  onCheckedChange={(v) => onPatch(row.day, { flags: { [k]: !!v } })} />
                {label}
              </label>
            ))}
          </div>
        </td>
        <td className="px-3 py-3 text-[10px] font-mono text-[#6b7280]">
          {locked ? <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> bloccato</span> : (
            <div className="flex flex-col items-start gap-1.5">
              <span>modificabile</span>
              <button data-testid={`open-leadgen-map-${row.day}`}
                onClick={() => window.dispatchEvent(new CustomEvent("open-leadgen", { detail: { cid: campaign.id, day: row.day } }))}
                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono uppercase text-white bg-[#E0262C] hover:bg-[#2B3036] transition-colors whitespace-nowrap">
                <MapIcon className="w-3 h-3" /> Ricerca con mappa
              </button>
            </div>
          )}
        </td>
      </tr>
      {open && (
        <tr className="bg-white">
          <td colSpan={8} className="p-0"><DayItemsEditor campaign={campaign} row={row} onChanged={onReload} /></td>
        </tr>
      )}
    </>
  );
};

const TotalsGrid = ({ title, totals, testid }) => (
  <div data-testid={testid} className="border border-[#d5dbde]">
    <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-mono text-[#6b7280] border-b border-[#d5dbde]">{title}</div>
    <div className="grid grid-cols-5 divide-x divide-[#d5dbde]">
      {METRICS.map(([k, label]) => (
        <div key={k} className="p-4">
          <div className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280]">{label}</div>
          <div data-testid={`${testid}-${k}`} className="font-display text-2xl mt-1">{totals?.[k] ?? 0}</div>
        </div>
      ))}
    </div>
  </div>
);

export const CampaignDataPanel = ({ id, onBack }) => {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminApi.managedDetail(id).then(setC).catch(() => toast.error("Errore caricamento campagna")), [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, msg) => {
    setBusy(true);
    try { setC(await adminApi.managedAction(id, action)); toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const patch = async (day, payload) => {
    try {
      await adminApi.updateDay(id, day, payload);
      toast.success(payload.flags ? "Visibilità aggiornata" : "Dato salvato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
  };

  if (!c) return <div className="border border-[#d5dbde] p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#E0262C]" /></div>;

  return (
    <div data-testid="mc-detail" className="space-y-6">
      <button data-testid="mc-back" onClick={onBack} className="text-sm text-[#5b6168] hover:text-[#E0262C] inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Tutte le campagne
      </button>

      <div className="border border-[#d5dbde] bg-white p-6 flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-2xl">{c.params?.skill} · <span className="text-[#5b6168]">{c.params?.area}</span></h2>
            <Badge map={CAMPAIGN_STATUS} value={c.status} testid="mc-detail-status" />
            {c.renewed_from && <span data-testid="mc-renewal-badge" className="text-[10px] font-mono uppercase border border-[#E0262C] text-[#E0262C] px-2 py-0.5">Rinnovo di #{c.renewed_from.slice(0, 8)}</span>}
          </div>
          <div className="text-xs font-mono text-[#6b7280] break-all">{c.landing_url}</div>
          {c.params?.notes && <div className="text-sm text-[#5b6168]">{c.params.notes}</div>}
          <div className="text-xs text-[#5b6168] font-mono">
            {c.client_name} · {c.client_email} · {money(c.price, c.currency)} pagati il {fmtDate(c.paid_at, true)}
          </div>
          <AddonsAdmin c={c} onChanged={load} />
          <div className="text-xs text-[#6b7280] font-mono">
            Target: {c.targeting?.gender === "all" ? "tutti" : c.targeting?.gender} · {c.targeting?.age_min}-{c.targeting?.age_max} anni
            {c.targeting?.interests?.length ? ` · ${c.targeting.interests.join(", ")}` : ""}
          </div>
        </div>
        <div className="w-full lg:w-80 space-y-3">
          <div className="grid grid-cols-3 text-center border border-[#d5dbde] divide-x divide-[#d5dbde]">
            <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#6b7280]">Inizio</div><div className="text-xs font-mono mt-1">{fmtDate(c.start_date)}</div></div>
            <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#6b7280]">Fine</div><div className="text-xs font-mono mt-1">{fmtDate(c.end_date)}</div></div>
            <div className="p-3"><div className="text-[9px] uppercase font-mono text-[#6b7280]">Rimanenti</div><div data-testid="mc-days-remaining" className="text-xs font-mono mt-1">{c.start_date ? `${c.days_remaining} gg` : "—"}</div></div>
          </div>
          <ProgressBar value={c.progress} testid="mc-progress" />
          <div className="text-[10px] font-mono text-[#6b7280]">Giorno {c.current_day}/{c.duration_days} · {c.progress}%{c.overdue && c.status !== "completed" ? " · durata raggiunta: chiudi la campagna" : ""}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {c.status === "awaiting_start" && (
          <button data-testid="mc-start-btn" disabled={busy} onClick={() => act("start", "Campagna avviata · cliente notificato")}
            className={`${actionBtn} aurora-gradient text-white hover:opacity-90`}><Play className="w-4 h-4" /> Avvia campagna</button>
        )}
        {c.status === "running" && (
          <button data-testid="mc-pause-btn" disabled={busy} onClick={() => act("pause", "Campagna in pausa")}
            className={`${actionBtn} border border-[#d5dbde] text-[#2B3036] hover:border-[#f59e0b]`}><Pause className="w-4 h-4" /> Metti in pausa</button>
        )}
        {c.status === "paused" && (
          <button data-testid="mc-resume-btn" disabled={busy} onClick={() => act("resume", "Campagna ripresa")}
            className={`${actionBtn} border border-[#d5dbde] text-[#2B3036] hover:border-[#10b981]`}><Play className="w-4 h-4" /> Riprendi</button>
        )}
        {c.editable && <CompleteButton busy={busy} onConfirm={() => act("complete", "Campagna completata · riepilogo inviato al cliente")} />}
        {c.status === "completed" && (
          <span data-testid="mc-locked-banner" className="h-10 px-4 text-sm inline-flex items-center gap-2 border border-[#d5dbde] text-[#5b6168]">
            <Lock className="w-4 h-4" /> Completata il {fmtDate(c.completed_at, true)} · inserimenti bloccati
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <TotalsGrid title="Totale raccolto (SU)" totals={c.status === "completed" ? c.summary : c.totals} testid="mc-totals" />
        <TotalsGrid title="Visibile al cliente (flaggato)" totals={c.totals_visible} testid="mc-totals-visible" />
      </div>

      {c.days.length ? (
        <div data-testid="mc-days-table" className="border border-[#d5dbde] overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
                {["Giorno", "Contatti", "IG", "TikTok", "Visual", "Click", "Flag visibilità cliente", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {c.days.map((r) => <DayRow key={r.day} row={r} campaign={c} onPatch={patch} onReload={load} />)}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-dashed border-[#d5dbde] p-10 text-center text-sm text-[#6b7280]">
          La tabella giornaliera apparirà quando avvii la campagna.
        </div>
      )}
    </div>
  );
};
